import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { ArrowLeft, Trash2, Phone, MessageSquare, NotebookPen } from "lucide-react";

const STATUSES = ["new", "contacted", "converted", "archived"];
const KINDS = [
  { v: "note", label: "Note", Icon: NotebookPen },
  { v: "call", label: "Call", Icon: Phone },
  { v: "meeting", label: "Meeting", Icon: MessageSquare },
];

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteKind, setNoteKind] = useState("note");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/leads/${id}`);
    setLead(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    const { data } = await api.patch(`/leads/${id}`, { status });
    setLead(data);
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/notes`, {
        text: noteText.trim(),
        kind: noteKind,
      });
      setLead(data);
      setNoteText("");
    } finally {
      setBusy(false);
    }
  };

  const deleteNote = async (noteId) => {
    await api.delete(`/leads/${id}/notes/${noteId}`);
    load();
  };

  const convertToClient = async () => {
    if (!window.confirm("Convert this lead into a client? They'll appear in the Clients section.")) return;
    const { data } = await api.post(`/leads/${id}/convert`);
    navigate(`/admin/clients/${data.id}`);
  };

  const deleteLead = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/leads/${id}`);
    navigate("/admin/leads");
  };

  if (!lead) return <p className="text-[#9c95a8]">Loading…</p>;

  return (
    <div data-testid="lead-detail-page">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 text-sm text-[#bdb6c4] hover:text-white mb-6">
        <ArrowLeft size={14} /> Back to leads
      </Link>

      <div className="bg-[#13121f] border border-white/10 rounded-2xl p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-bold tracking-tight">{lead.name}</h1>
            <p className="text-[#bdb6c4] mt-1">
              <a href={`mailto:${lead.email}`} className="hover:text-[#f5c76e]">{lead.email}</a>
            </p>
            <p className="font-mono text-[10px] text-[#6b6677] mt-2">
              {new Date(lead.created_at).toLocaleString()} · via {lead.source}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`status-${s}`}
                className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.18em] border transition-colors ${
                  lead.status === s
                    ? "bg-[#f5c76e] text-[#0a0a14] border-[#f5c76e]"
                    : "border-white/10 text-[#bdb6c4] hover:bg-white/[0.04]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-[#0a0a14] border border-white/5 rounded-xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6b6677] mb-2">The postcard</p>
          <p className="font-serif italic text-lg leading-relaxed" style={{ fontFamily: "Fraunces, serif" }}>
            "{lead.idea}"
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {lead.status !== "converted" && (
            <button
              onClick={convertToClient}
              data-testid="convert-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#f5c76e] text-[#0a0a14] font-semibold text-sm hover:bg-[#ffd485] transition-colors"
            >
              Convert to client →
            </button>
          )}
          {lead.converted_client_id && (
            <Link
              to={`/admin/clients/${lead.converted_client_id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#f5c76e]/40 text-[#f5c76e] font-semibold text-sm hover:bg-[#f5c76e]/10 transition-colors"
            >
              View client →
            </Link>
          )}
          <button
            onClick={deleteLead}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-500/30 text-red-300 text-sm hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-8">
        <h2 className="font-display text-xl font-bold mb-4">Notes &amp; logs</h2>

        <form onSubmit={addNote} className="bg-[#13121f] border border-white/10 rounded-2xl p-5 mb-4" data-testid="note-form">
          <div className="flex flex-wrap gap-2 mb-3">
            {KINDS.map(({ v, label, Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => setNoteKind(v)}
                data-testid={`note-kind-${v}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  noteKind === v
                    ? "bg-[#f5c76e]/10 text-[#f5c76e] border-[#f5c76e]/40"
                    : "border-white/10 text-[#bdb6c4] hover:bg-white/[0.04]"
                }`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            data-testid="note-input"
            rows={3}
            placeholder={
              noteKind === "call" ? "Phone call: discussed scope, timeline…" :
              noteKind === "meeting" ? "Meeting notes: who, when, what we agreed…" :
              "Quick note for yourself…"
            }
            className="w-full bg-[#0a0a14] border border-white/10 rounded-xl px-3 py-2.5 text-[#f4ede1] outline-none focus:border-[#f5c76e]/60 transition-colors resize-none"
          />
          <button
            type="submit"
            disabled={busy || !noteText.trim()}
            data-testid="note-submit"
            className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#f5c76e] text-[#0a0a14] font-semibold text-sm hover:bg-[#ffd485] transition-colors disabled:opacity-60"
          >
            Add {noteKind}
          </button>
        </form>

        <div className="space-y-2" data-testid="notes-list">
          {(lead.notes || []).slice().reverse().map((n) => (
            <div key={n.id} className="bg-[#13121f] border border-white/10 rounded-xl px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded border border-[#f5c76e]/40 text-[#f5c76e]">
                    {n.kind}
                  </span>
                  <span className="font-mono text-[10px] text-[#6b6677]">
                    {new Date(n.created_at).toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={() => deleteNote(n.id)}
                  className="text-[#6b6677] hover:text-red-300 transition-colors"
                  title="Delete note"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-[#f4ede1] whitespace-pre-wrap leading-relaxed">{n.text}</p>
            </div>
          ))}
          {(!lead.notes || lead.notes.length === 0) && (
            <p className="text-[#9c95a8] text-sm">No notes yet — add the first one above.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
